import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AppointmentCheckoutData,
  AppointmentInterface,
  BookingSteps,
  BookServiceSteps,
  DiscountCode,
  ExtendedAddonInterface,
  GiftCardInterface,
  ProductItem as ProductItemType,
  ProviderInterface,
  SelectedProductType,
  ServiceInterface,
  UpcomingAppointment
} from 'types';

export type BookingAppointmentStore = {
  isContinueBooking: boolean;
  isAuthRedirection: boolean;
  redirectParams: string;
  activeStep: BookingSteps;
  moveApptId: number | null;
  selectedServices: ServiceInterface[];
  selectedProviders: Record<number, ProviderInterface | null>;
  selectedAddons: Record<number, ExtendedAddonInterface[]>;
  selectedAppointment: AppointmentInterface | null;
  promoData: DiscountCode | null;
  promoCode: string;
  comments: string;
  apptCalendarDate: { start: string; end: string };
  checkoutData: AppointmentCheckoutData | null;
  recommendedProducts: ProductItemType[];
  selectedProducts: SelectedProductType[];
  selectedDepositGiftCards: GiftCardInterface[];
  depositAmount: number;
  serviceActiveStep: BookServiceSteps;
  referalLink: string;
  cancelationAppointment: UpcomingAppointment | null;
  cancelationLoading: boolean;
  cancelationErrorMessage: string;
  cancellationPolicyModalOpen: boolean;
};

const initialState: BookingAppointmentStore = {
  isContinueBooking: false,
  isAuthRedirection: false,
  redirectParams: '',
  moveApptId: null,
  activeStep: BookingSteps.LOCATION,
  selectedServices: [],
  selectedProviders: {},
  selectedAddons: {},
  selectedAppointment: null,
  promoData: null,
  promoCode: '',
  comments: '',
  apptCalendarDate: { start: '', end: '' },
  checkoutData: null,
  recommendedProducts: [],
  selectedProducts: [],
  selectedDepositGiftCards: [],
  depositAmount: 0,
  serviceActiveStep: BookServiceSteps.Services,
  referalLink: '',
  cancelationAppointment: null,
  cancelationLoading: false,
  cancelationErrorMessage: '',
  cancellationPolicyModalOpen: false
};

export const bookingAppointmentSlice = createSlice({
  name: 'bookingAppointment',
  initialState,
  reducers: {
    setIsContinueBooking: (state, action: PayloadAction<boolean>) => {
      state.isContinueBooking = action.payload;
    },
    setIsAuthRedirection: (state, action: PayloadAction<boolean>) => {
      state.isAuthRedirection = action.payload;
    },
    setRedirectParams: (state, action: PayloadAction<string>) => {
      state.redirectParams = action.payload;
    },
    setStep: (state, action: PayloadAction<BookingSteps>) => {
      state.activeStep = action.payload;
    },
    setMoveApptId: (state, action: PayloadAction<number>) => {
      state.moveApptId = action.payload;
    },
    selectService: (state, action: PayloadAction<ServiceInterface>) => {
      state.selectedServices.push(action.payload);
    },
    selectServiceList: (state, action: PayloadAction<ServiceInterface[]>) => {
      state.selectedServices = action.payload;
    },
    removeService: (state, action: PayloadAction<number>) => {
      state.selectedServices = state.selectedServices.filter(
        (service) => service.id !== action.payload
      );
    },
    clearService: (state) => {
      state.selectedProviders = {};
      state.selectedServices = [];
      state.serviceActiveStep = BookServiceSteps.Services;
    },
    setProvider: (
      state,
      action: PayloadAction<{ service: number; provider: ProviderInterface }>
    ) => {
      state.selectedProviders[action.payload.service] = action.payload.provider;
    },
    removeProvider: (state, action: PayloadAction<{ service: number }>) => {
      const updatedProviders = { ...state.selectedProviders };
      delete updatedProviders[action.payload.service];
      return { ...state, selectedProviders: updatedProviders };
    },
    setAddons: (
      state,
      action: PayloadAction<{
        service: number;
        addons: ExtendedAddonInterface[];
      }>
    ) => {
      state.selectedAddons[action.payload.service] = action.payload.addons;
    },
    removeAddons: (state, action: PayloadAction<{ service: number }>) => {
      const { service } = action.payload;
      const addons = state.selectedAddons[service];
      if (addons) {
        addons.forEach((addon) => {
          delete state.selectedProviders[addon.id];
        });
      }
      delete state.selectedAddons[service];
    },
    setAppointment: (
      state,
      action: PayloadAction<AppointmentInterface | null>
    ) => {
      state.selectedAppointment = action.payload;
    },
    setPromoData: (state, action: PayloadAction<DiscountCode | null>) => {
      state.promoData = action.payload;
    },
    setPromoCode: (state, action: PayloadAction<string>) => {
      state.promoCode = action.payload;
    },
    setComments: (state, action: PayloadAction<string>) => {
      state.comments = action.payload;
    },
    setApptCalendarDate: (
      state,
      action: PayloadAction<{ start: string; end: string }>
    ) => {
      state.apptCalendarDate = action.payload;
    },
    setCheckoutData: (
      state,
      action: PayloadAction<AppointmentCheckoutData>
    ) => {
      state.checkoutData = action.payload;
    },
    setRecommendedProducts: (
      state,
      action: PayloadAction<ProductItemType[]>
    ) => {
      state.recommendedProducts = action.payload;
    },
    setSelectedRecommendedProducts: (
      state,
      action: PayloadAction<SelectedProductType[]>
    ) => {
      state.selectedProducts = action.payload;
    },
    setDepositAmount: (state, action: PayloadAction<number>) => {
      state.depositAmount = action.payload;
    },
    selectDepositGiftCards: (
      state,
      action: PayloadAction<GiftCardInterface>
    ) => {
      const isExist = state.selectedDepositGiftCards.some(
        ({ id }) => id === action.payload.id
      );

      if (isExist) {
        const item = state.selectedDepositGiftCards.find(
          ({ id }) => id === action.payload.id
        );

        state.depositAmount += item?.amt_balance ?? 0;
        state.selectedDepositGiftCards = [
          ...state.selectedDepositGiftCards
        ].filter(({ id }) => id !== action.payload.id);
      } else if (state.depositAmount > action.payload.amt_balance) {
        state.depositAmount -= action.payload.amt_balance;
        state.selectedDepositGiftCards = [
          ...state.selectedDepositGiftCards,
          action.payload
        ];
      } else {
        const giftCadr = {
          ...action.payload,
          amt_balance: state.depositAmount
        };
        state.depositAmount = 0;
        state.selectedDepositGiftCards = [
          ...state.selectedDepositGiftCards,
          giftCadr
        ];
      }
    },
    clearSelectedCards: (state) => {
      state.selectedDepositGiftCards = [];
    },
    setServiceActiveStep: (state, action: PayloadAction<BookServiceSteps>) => {
      state.serviceActiveStep = action.payload;
    },
    setReferalLink: (state, action: PayloadAction<string>) => {
      state.referalLink = action.payload;
    },
    setCancelationLoading: (state, action: PayloadAction<boolean>) => {
      state.cancelationLoading = action.payload;
    },
    setCancelationAppointment: (
      state,
      action: PayloadAction<UpcomingAppointment | null>
    ) => {
      state.cancelationAppointment = action.payload;
    },
    setCancelationErrorMessage: (state, action: PayloadAction<string>) => {
      state.cancelationErrorMessage = action.payload;
    },
    setCancellationPolicyModalOpen: (state, action: PayloadAction<boolean>) => {
      state.cancellationPolicyModalOpen = action.payload;
    },
    resetForm: (state) => ({
      ...initialState,
      activeStep: state.activeStep
    })
  }
});

export const {
  setStep,
  selectService,
  selectServiceList,
  setIsAuthRedirection,
  removeService,
  setProvider,
  removeProvider,
  setAppointment,
  setPromoData,
  setAddons,
  removeAddons,
  setPromoCode,
  setComments,
  setApptCalendarDate,
  resetForm,
  setCheckoutData,
  setMoveApptId,
  setIsContinueBooking,
  setRedirectParams,
  clearService,
  setRecommendedProducts,
  setSelectedRecommendedProducts,
  selectDepositGiftCards,
  setDepositAmount,
  setServiceActiveStep,
  setReferalLink,
  clearSelectedCards,
  setCancelationAppointment,
  setCancelationErrorMessage,
  setCancelationLoading,
  setCancellationPolicyModalOpen
} = bookingAppointmentSlice.actions;

export default bookingAppointmentSlice.reducer;
